import Color from 'color';
import * as fabric from 'fabric';
import { v4 as uuid } from 'uuid';
import { FabricObject } from '../models';
import { fitTextToRect, registerFabricClass, resolveFromObject, toObject } from '../utils';
import { CustomControlObject } from './CustomControl';
import FromPort from './FromPort';
import { LinkObject } from './Link';
import { PortObject } from './Port';
import Spinner from './Spinner';
import ToPort from './ToPort';

export const OUT_PORT_TYPE = {
	SINGLE: 'SINGLE',
	STATIC: 'STATIC',
	DYNAMIC: 'DYNAMIC',
	BROADCAST: 'BROADCAST',
	NONE: 'NONE',
};

export type NodeObject = FabricObject<fabric.Group> & {
	errorFlag?: fabric.IText;
	label?: fabric.Text;
	color?: string;
	errors?: any;
	toPort?: PortObject;
	fromPort?: PortObject[];
	descriptor?: Record<string, any>;
	ports?: PortObject[];
	nodeClazz?: string;
	configuration?: Record<string, any>;
	handlers?: PortObject[];
	customControls?: CustomControlObject[];
	defaultPortOption?: () => Partial<PortObject>;
	toPortOption?: () => Partial<PortObject>;
	fromPortOption?: () => Partial<PortObject>;
	createCustomControls?: (left: number, top: number) => CustomControlObject[];
	createToPort?: (left: number, top: number) => PortObject;
	createFromPort?: (left: number, top: number) => PortObject[];
	setErrors?: (errors: any) => void;
	setName?: (name: string) => void;
	select?: () => void;
	unselect?: () => void;
	duplicate?: () => NodeObject;
};

export const serializeNodeThemeProperties = (node: { get(key: string): unknown }) => ({
	actionButtonColor: node.get('actionButtonColor'),
	actionButtonIconColor: node.get('actionButtonIconColor'),
	labelColor: node.get('labelColor'),
	portFill: node.get('portFill'),
	routeFill: node.get('routeFill'),
	routeStroke: node.get('routeStroke'),
	routeTextColor: node.get('routeTextColor'),
});

class Node extends fabric.Group {
	static type = 'node';
	superType = 'node';
	declare id: string;
	declare label: fabric.Text;
	declare rect: fabric.Rect;
	declare nodeIcon: fabric.Group;
	declare errorFlag: fabric.Group;
	declare button?: fabric.Group;
	declare color?: string;
	declare descriptor: Record<string, any>;
	declare customControls?: CustomControlObject[];
	declare toPort?: PortObject;
	declare fromPort?: PortObject[];
	declare originStroke?: string;
	declare fontSize?: number;
	declare fontFamily?: string;
	declare actionButtonColor?: string;
	declare actionButtonIconColor?: string;
	declare labelColor?: string;
	declare portFill?: string;
	declare routeFill?: string;
	declare routeStroke?: string;
	declare routeTextColor?: string;

	private static createIconBoxPath(options: any, radius: number) {
		const { width, height, ...other } = options;
		const path = [
			`M ${radius} 0`,
			`Q 0 0 0 ${radius}`,
			`L 0 ${height}`,
			`L ${width - radius} ${height}`,
			`Q ${width} ${height} ${width} ${height - radius}`,
			`L ${width} 0`,
			`Z`,
		].join(' ');
		return new fabric.Path(path, other);
	}

	private static createNodeIcon(options: any) {
		const { h, s, v } = Color(options.color).hsv().object();
		const iconBox = new fabric.Rect({
			width: 48,
			height: 48,
			rx: 10,
			ry: 10,
			left: 0,
			top: 0,
			originX: 'center',
			originY: 'center',
			strokeWidth: 0,
		});
		iconBox.set(
			'fill',
			new fabric.Gradient({
				type: 'linear',
				coords: { x1: 0, y1: 0, x2: 0, y2: 40 },
				colorStops: [
					{ offset: 0, color: options.color },
					{ offset: 1, color: Color.hsv(h, s, v - 60).hex() },
				],
			}),
		);
		const icon = new fabric.IText(options.icon || '\uE174', {
			fontFamily: 'Font Awesome 5 Free',
			fontWeight: 900,
			fontSize: 24,
			fill: '#fff',
			left: 0,
			top: 0,
			originX: 'center',
			originY: 'center',
		});
		return new fabric.Group([iconBox, icon], {
			originX: 'center',
			originY: 'center',
		});
	}

	private static createErrorFlag() {
		const icon = new fabric.IText('\uf071', {
			fontFamily: 'Font Awesome 5 Free',
			fontWeight: 900,
			fontSize: 12,
			fill: '#fff',
			left: 0,
			top: 0,
			originX: 'center',
			originY: 'center',
		});
		const box = Node.createIconBoxPath({ width: 20, height: 20, fill: 'red', strokeWidth: 0 }, 12);
		icon.set({ left: box.width / 2, top: box.height / 2 });
		return new fabric.Group([box, icon], {
			left: 0,
			top: 0,
			originX: 'center',
			originY: 'center',
		});
	}

	private static createActionButton(options: any) {
		const width = 24;
		const height = 60;
		const radius = 12;
		const r = Math.min(radius, height / 2, width / 2);
		const path = [
			'M 0 0',
			`L ${width - r} 0`,
			`A ${r} ${r} 0 0 1 ${width} ${r}`,
			`L ${width} ${height - r}`,
			`A ${r} ${r} 0 0 1 ${width - r} ${height}`,
			`L 0 ${height}`,
			'Z',
		].join(' ');
		const box = new fabric.Path(path, { fill: options.actionButtonColor || '#5f646b' });
		const icon = new fabric.IText('\uf04b', {
			fontFamily: 'Font Awesome 5 Free',
			fontWeight: 900,
			fontSize: 14,
			fill: options.actionButtonIconColor || '#fff',
			left: box.width / 2,
			top: box.height / 2,
			originX: 'center',
			originY: 'center',
		});
		return new fabric.Group([box, icon], {
			hoverCursor: 'pointer',
			originX: 'center',
			originY: 'center',
		});
	}

	private static layoutContents(
		rect: fabric.Rect,
		nodeIcon: fabric.Group,
		label: fabric.Text,
		errorFlag: fabric.Group,
		button?: fabric.Group,
	) {
		const rectLeft = rect.left - rect.width / 2;
		const rectTop = rect.top - rect.height / 2;
		nodeIcon.set({
			left: rectLeft + 8 + nodeIcon.width / 2,
			top: rectTop + 7 + nodeIcon.height / 2,
		});
		label.set({
			left: nodeIcon.left + nodeIcon.width / 2 + 10 + label.width / 2,
			top: rect.top,
		});
		errorFlag.set({
			left: rectLeft + errorFlag.width / 2,
			top: rectTop + errorFlag.height / 2,
		});
		if (button) {
			button.set({
				left: rect.left + rect.width / 2 - button.width / 2 + 1,
				top: rect.top + 1,
			});
		}
		[nodeIcon, label, errorFlag, button].forEach(object => object?.setCoords());
	}

	constructor(options: any = {}) {
		let name = options.name || 'Default Node';
		let fontSize = options.fontSize || 16;
		const fontFamily = options.fontFamily || 'Noto Sans';
		if (options.name) {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			const fitted = fitTextToRect(
				ctx!,
				options.name,
				fontSize,
				fontFamily,
				options.descriptor?.actionButton ? 142 : 168,
				48,
			);
			name = fitted.text;
			fontSize = fitted.fontSize;
		}
		const label = new fabric.Text(name || 'Default Node', {
			fontSize,
			fontFamily,
			fontWeight: 400,
			fill: options.labelColor || '#fff',
		});
		const rect = new fabric.Rect({
			rx: 12,
			ry: 12,
			width: 240,
			height: 60,
			strokeWidth: 1,
			fill: options.fill,
			stroke: options.stroke,
		});
		const nodeIcon = Node.createNodeIcon(options);
		const errorFlag = Node.createErrorFlag();
		const node = [rect, nodeIcon, label, errorFlag];
		const button = options.descriptor?.actionButton ? Node.createActionButton(options) : undefined;
		if (button) {
			node.push(button);
		}
		Node.layoutContents(rect, nodeIcon, label, errorFlag, button);
		const nextOptions = Object.assign({}, options, {
			id: options.id || uuid(),
			width: 240,
			height: 60,
			hasRotatingPoint: false,
			hasControls: false,
			fontSize,
			fontFamily,
			color: options.color,
			subTargetCheck: !!options.descriptor?.actionButton,
			originStroke: options.stroke,
		});
		const { type: _type, objects: _objects, layoutManager: _layoutManager, ...groupOptions } = nextOptions;
		super(node, groupOptions);
		this.label = label;
		this.rect = rect;
		this.nodeIcon = nodeIcon;
		this.errorFlag = errorFlag;
		this.button = button;
		this.setErrors(options.errors);
	}

	defaultPortOption() {
		const portFill = this.portFill || '#5f646b';
		return {
			nodeId: this.id,
			hasBorders: false,
			hasControls: false,
			hasRotatingPoint: false,
			selectable: false,
			originX: 'center',
			originY: 'center',
			lockScalingX: true,
			lockScalingY: true,
			superType: 'port',
			connectedFill: this.color || portFill,
			disabledFill: 'red',
			enabledFill: 'green',
			originFill: portFill,
			fill: portFill,
			hoverCursor: 'pointer',
			strokeWidth: 2,
			stroke: portFill,
			links: [] as LinkObject[],
			enabled: true,
		};
	}

	toPortOption() {
		return {
			...this.defaultPortOption(),
			height: 6,
			width: 24,
		};
	}

	fromPortOption() {
		return {
			...this.defaultPortOption(),
			radius: 12,
		};
	}

	createCustomControls(left: number, top: number) {
		const spinner = new Spinner({
			left,
			top,
			radius: 12,
			fill: '#FFD700',
			transaciton: false,
		});
		this.customControls = [spinner as unknown as CustomControlObject];
		spinner.setPosition(left, top);
		return this.customControls;
	}

	createToPort(left: number, top: number) {
		if (this.descriptor.inEnabled) {
			this.toPort = new ToPort({
				id: 'defaultInPort',
				...this.toPortOption(),
				left,
				top,
			}) as unknown as PortObject;
			this.toPort.setPosition(left, top);
		}
		return this.toPort;
	}

	createFromPort(left: number, top: number) {
		if (this.descriptor.outPortType === OUT_PORT_TYPE.STATIC) {
			const offset = 60;
			this.fromPort = this.descriptor.outPorts.map((outPort: string, index: number) => {
				const labelColor = index === 0 ? '#ff3030' : '#15cc08';
				const targetLeft = index === 0 ? left - offset : left + offset;
				const port = new FromPort({
					id: outPort,
					left: targetLeft,
					top,
					leftDiff: index === 0 ? -offset : offset,
					...this.fromPortOption(),
					label: outPort,
					color: labelColor,
					fontSize: 14,
					fontFamily: 'Noto Sans',
				});
				port.setPosition(targetLeft, top);
				return port;
			});
		} else if (
			this.descriptor.outPortType === OUT_PORT_TYPE.DYNAMIC ||
			this.descriptor.outPortType === OUT_PORT_TYPE.NONE
		) {
			this.fromPort = [];
		} else {
			const port = new FromPort({
				id: 'defaultFromPort',
				...this.fromPortOption(),
				left,
				top,
			}) as unknown as PortObject;
			port.setPosition(left, top);
			this.fromPort = [port];
		}
		return this.fromPort;
	}

	setErrors(errors: any) {
		this.set({ errors });
		if (errors) {
			this.errorFlag.set({ visible: true });
			this.rect.set({ stroke: 'red' });
		} else {
			this.errorFlag.set({ visible: false });
			this.rect.set({ stroke: this.originStroke });
		}
	}

	setName(name: string) {
		const context = this.canvas?.getContext();
		if (!context) {
			return;
		}
		const fitted = fitTextToRect(
			context,
			name,
			this.fontSize,
			this.fontFamily,
			this.descriptor.actionButton ? 142 : 168,
			48,
		);
		this.label.set({
			fontSize: fitted.fontSize,
			text: fitted.text,
		});
		Node.layoutContents(this.rect, this.nodeIcon, this.label, this.errorFlag, this.button);
	}

	select() {
		this.rect.set({ strokeDashArray: [3, 3], strokeWidth: 2 });
		this.canvas?.requestRenderAll();
	}

	unselect() {
		this.rect.set({ strokeDashArray: null, strokeWidth: 1 });
		this.canvas?.requestRenderAll();
	}

	duplicate() {
		const options = this.toObject();
		options.id = uuid();
		options.name = `${options.name}_clone`;
		const NodeConstructor = this.constructor as typeof Node;
		return new NodeConstructor(options) as unknown as NodeObject;
	}

	toObject(propertiesToInclude: any[] = []) {
		return toObject(super.toObject(propertiesToInclude), this, propertiesToInclude, {
			id: this.get('id'),
			name: this.get('name'),
			icon: this.get('icon'),
			color: this.get('color'),
			fontSize: this.get('fontSize'),
			fontFamily: this.get('fontFamily'),
			description: this.get('description'),
			superType: this.get('superType'),
			configuration: this.get('configuration'),
			nodeClazz: this.get('nodeClazz'),
			descriptor: this.get('descriptor'),
			borderColor: this.get('borderColor'),
			borderScaleFactor: this.get('borderScaleFactor'),
			dblclick: this.get('dblclick'),
			deletable: this.get('deletable'),
			cloneable: this.get('cloneable'),
			fromPort: this.get('fromPort'),
			toPort: this.get('toPort'),
			errors: this.get('errors'),
			...serializeNodeThemeProperties(this),
		});
	}

	_render(ctx: CanvasRenderingContext2D) {
		super._render(ctx);
	}

	static fromObject(options: any, callback?: any) {
		return resolveFromObject(new Node(options) as unknown as NodeObject, callback);
	}
}

registerFabricClass('Node', Node);

export default Node;
